import React, {useCallback, useContext, useEffect, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory} from "react-router-dom";
import {WireTemplatesContext} from "./context";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";
import {
    getClearingSystemIdWithPrefix,
    getWireTemplateStepUrl,
    shouldNavigateToWireTemplateStep,
    USABA_CLEARING_SYSTEM_CODE,
    USABA_CLEARING_SYSTEM_ID_PREFIX,
    WIRE_TEMPLATE_STEP_URL
} from "./AddNewWireTemplateStep1";


const creditorAgentFields = [
    [
        {label: 'Agent BIC', name: 'creditorAgentBIC', md: 3, maxLength: 50, conditionallyRequired: true},
        {label: 'Clearing System Code', name: 'creditorAgentClearingSystemCode', md: 2, type: 'select', options: [{label: 'USABA', value: 'USABA'}], conditionallyRequired: true},
        {label: 'Clearing System ID', name: 'creditorAgentClearingSystemID', md: 4, maxLength: 50, conditionallyRequired: true},
        {label: 'Account IBAN', name: 'creditorAgentAccountIBAN', md: 3, maxLength: 50},
    ],
    [
        {label: 'Creditor Agent Name', name: 'creditorAgentName', md: 4, maxLength: 255, required: true},
        {label: 'Agent Department', name: 'creditorAgentDepartment', md: 4, maxLength: 70},
        {label: 'Agent Sub Department', name: 'creditorAgentSubDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'creditorAgentStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'creditorAgentBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'creditorAgentBuildingName', md: 4, maxLength: 35},
        {label: 'Agent Floor', name: 'creditorAgentFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'creditorAgentPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'creditorAgentRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'creditorAgentPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'creditorAgentTownName', md: 3, maxLength: 35, conditionallyRequired: true},
        {label: 'Town Location Name', name: 'creditorAgentTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'creditorAgentDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'creditorAgentProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'creditorAgentCountry', md: 3, maxLength: 2, type: 'country', conditionallyRequired: true},
    ],
];

const CREDITOR_AGENT_IDENTIFIER_ERROR = 'ERROR. Please check the Note above to fill in the required fields.';
const CREDITOR_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR = `Clearing System ID must start with ${USABA_CLEARING_SYSTEM_ID_PREFIX} followed by digits`;

const hasValue = (value) => value !== undefined && value !== null && value.toString().trim() !== '';
const isValidUSABAClearingSystemID = (clearingSystemID) => new RegExp(`^${USABA_CLEARING_SYSTEM_ID_PREFIX}\\d+$`).test(clearingSystemID.toString().trim());

export const getCreditorAgentClearingSystemIDWithPrefix = (clearingSystemID) => {
    if (!clearingSystemID) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    const clearingSystemIDValue = clearingSystemID.toString();
    const clearingSystemIDValueUpperCase = clearingSystemIDValue.toUpperCase();

    if (USABA_CLEARING_SYSTEM_ID_PREFIX.startsWith(clearingSystemIDValueUpperCase) && clearingSystemIDValueUpperCase.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    return getClearingSystemIdWithPrefix(clearingSystemIDValue);
}

export const validateStep2 = (addWireTemplateState = {}) => {
    let errors = {}
    const {
        creditorAgentBIC,
        creditorAgentClearingSystemCode,
        creditorAgentClearingSystemID,
        creditorAgentName,
        creditorAgentTownName,
        creditorAgentCountry,
    } = addWireTemplateState;

    if (!hasValue(creditorAgentName)) {
        errors['creditorAgentName'] = 'Please enter creditor agent name'
    }

    const hasBIC = hasValue(creditorAgentBIC);
    const hasClearingSystemCode = hasValue(creditorAgentClearingSystemCode);
    const hasClearingSystemID = hasValue(creditorAgentClearingSystemID);
    const hasTown = hasValue(creditorAgentTownName);
    const hasCountry = hasValue(creditorAgentCountry);

    if (!hasBIC && (!hasClearingSystemCode || !hasClearingSystemID || !hasTown || !hasCountry)) {
        errors['creditorAgentBIC'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        if (!hasClearingSystemCode) {
            errors['creditorAgentClearingSystemCode'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
        if (!hasClearingSystemID) {
            errors['creditorAgentClearingSystemID'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
        if (!hasTown) {
            errors['creditorAgentTownName'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
        if (!hasCountry) {
            errors['creditorAgentCountry'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
    }

    if (creditorAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE && hasClearingSystemID && !isValidUSABAClearingSystemID(creditorAgentClearingSystemID)) {
        errors['creditorAgentClearingSystemID'] = CREDITOR_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR
    }

    return errors
}

export default function AddNewWireTemplateStep2() {

    const history = useHistory();
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState} = useContext(WireTemplatesContext);
    const {makeGetRequestNoParams} = useContext(SecurityContext);
    const [countries, setCountries] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    const currentStepPage = '2'
    const backStepPage = '1'
    const nextStepPage = '3'

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = (err) => {
            console.log("Error on get unique iso country code country name: ", err);
        }

        makeGetRequestNoParams(URL.COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    useEffect(() => {
        getCountries()
    }, [getCountries])
    useEffect(() => {
        if (shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history])

    const handleChange = (e) => {
        const {name, value} = e.currentTarget;
        const newAddWireTemplateState = {...addWireTemplateState, [name]: value};

        if (name === 'creditorAgentClearingSystemID' && addWireTemplateState.creditorAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE) {
            newAddWireTemplateState.creditorAgentClearingSystemID = getCreditorAgentClearingSystemIDWithPrefix(value);
        }

        if (name === 'creditorAgentClearingSystemCode' && value === USABA_CLEARING_SYSTEM_CODE) {
            newAddWireTemplateState.creditorAgentClearingSystemID = getCreditorAgentClearingSystemIDWithPrefix(addWireTemplateState.creditorAgentClearingSystemID);
        }

        setAddWireTemplateState(newAddWireTemplateState);
        if (formErrors[name]) {
            setFormErrors(previousErrors => ({...previousErrors, [name]: ''}))
        }
    }

    const handleBlur = (e) => {
        const {name, value} = e.currentTarget;

        if (name === 'creditorAgentClearingSystemID' && addWireTemplateState.creditorAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE && !value) {
            setAddWireTemplateState({
                ...addWireTemplateState,
                creditorAgentClearingSystemID: getCreditorAgentClearingSystemIDWithPrefix(value),
            });
        }
    }

    const handleFocus = (e) => {
        const {name, value} = e.currentTarget;

        if (name === 'creditorAgentClearingSystemID' && addWireTemplateState.creditorAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE && !value) {
            setAddWireTemplateState({
                ...addWireTemplateState,
                creditorAgentClearingSystemID: getCreditorAgentClearingSystemIDWithPrefix(value),
            });
        }
    }

    const handleClear = (e) => {
        e.preventDefault();
        clearAddNewWireTemplateState();
        setFormErrors({});
    }

    const handleBack = (e) => {
        e.preventDefault();
        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=backStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + backStepPage);
    }

    const handleNext = (e) => {
        e.preventDefault();

        const errors = validateStep2(addWireTemplateState);
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            return
        }

        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']= nextStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + nextStepPage);
    }

    const renderRequiredMarker = (field) => {
        if (field.required) {
            return <span className='red-cell'>*</span>;
        }

        return field.conditionallyRequired ? <span className='grey-cell'>*</span> : null;
    };

    const renderField = (field) => (
        <Col md={field.md} key={field.name}>
            <Form.Group controlId={`formWireTemplateStep2${field.name}`} className={field.type === 'country' || field.type === 'select' ? 'td-select' : ''}>
                <Form.Label column={false} className="filter-input-label">{field.label} {renderRequiredMarker(field)}</Form.Label>
                {field.type === 'country' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                        ))}
                    </Form.Control> : field.type === 'select' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {field.options.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Control> :
                    <Form.Control
                        name={field.name}
                        value={addWireTemplateState[field.name] || ''}
                        maxLength={field.maxLength}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        isInvalid={!!formErrors[field.name]}
                    />
                }
                <Form.Control.Feedback type={'invalid'}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[field.name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    )

    const hasFormErrors = Object.keys(formErrors).some(Boolean);

    return (
        <WireTemplateWizardLayout currentStep={2} stepTitle="Creditor Agent Details">
            <ShiftEnterPopupMessage />
            <Form>
                <div className={`awt-note ${hasFormErrors ? 'awt-note--error' : ''}`} role="note">
                    <b>Note:</b> Please populate CreditorAgentBIC and CreditorAgentName OR CreditorAgentClearingSystemCode, CreditorAgentClearingSystemIDs, CreditorAgentName, CreditorAgentTown and CreditorAgentCountry.
                    <p></p>
                    <b>Note:</b> Please populate Creditor Agent Street Name and Creditor Agent Province where possible.
                </div>
                {creditorAgentFields.map((fieldRow, index) => (
                    <Row key={index}>
                        {fieldRow.map(renderField)}
                    </Row>
                ))}

                <div className="awt-actions">
                    <button className="btn td-btn-secondary-clear" onClick={handleClear}>Clear</button>
                    <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                    <button className="btn td-btn-primary-light" onClick={handleNext}>Next</button>
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}

